import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, COPYRIGHT_STATUS } from "../../lib/api";
import { formatApiError } from "../../lib/auth";
import { StatusBadge } from "../../components/StatusBadge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { toast } from "sonner";
import { Shield, Search, Youtube, Edit3 } from "lucide-react";

export default function AdminCopyright() {
  const [params] = useSearchParams();
  const [records, setRecords] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState(params.get("status") || "");
  const [editing, setEditing] = useState(null);

  const load = () => {
    const p = {};
    if (q) p.q = q; if (status) p.status = status;
    api.get("/admin/copyright", { params: p }).then((r) => setRecords(r.data));
  };
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [q, status]);

  const save = async () => {
    try {
      await api.patch(`/admin/copyright/${editing.id}`, { status: editing.status, admin_notes: editing.admin_notes });
      toast.success("Updated"); setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
      <div className="text-xs font-semibold tracking-[0.2em] uppercase text-cyan-400">Administrator</div>
      <h1 className="mt-2 text-4xl font-bold tracking-tight">Copyright Requests</h1>

      <div className="mt-8 grid md:grid-cols-3 gap-3">
        <div className="relative md:col-span-2">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <Input placeholder="Search ID, song, ISRC or UPC..." value={q} onChange={(e) => setQ(e.target.value)}
            data-testid="cr-admin-search" className="pl-9 bg-zinc-900 border-zinc-800" />
        </div>
        <Select value={status || "all"} onValueChange={(v) => setStatus(v === "all" ? "" : v)}>
          <SelectTrigger data-testid="cr-admin-status-filter" className="bg-zinc-900 border-zinc-800"><SelectValue placeholder="All statuses" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {COPYRIGHT_STATUS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="mt-6 rounded-2xl border border-zinc-800 bg-zinc-950/60 overflow-x-auto">
        <table className="w-full min-w-[900px]">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
              <th className="p-4">Request ID</th>
              <th className="p-4">User</th>
              <th className="p-4">Song</th>
              <th className="p-4">ISRC/UPC</th>
              <th className="p-4">YouTube</th>
              <th className="p-4">Submitted</th>
              <th className="p-4">Status</th>
              <th className="p-4"></th>
            </tr>
          </thead>
          <tbody>
            {records.map((r) => (
              <tr key={r.id} data-testid={`cr-admin-row-${r.id}`}
                className={`border-b border-zinc-900 hover:bg-zinc-900/40 ${r.status === "Pending" ? "bg-amber-500/5" : ""}`}>
                <td className="p-4 font-mono text-cyan-400 text-sm">{r.id}</td>
                <td className="p-4 text-sm">{r.user?.full_name}<br /><span className="text-xs text-zinc-500">{r.user?.phone}</span></td>
                <td className="p-4">{r.song_title}</td>
                <td className="p-4 font-mono text-xs text-emerald-400">{r.isrc || "—"}<br />{r.upc || "—"}</td>
                <td className="p-4">
                  <a href={r.youtube_url} target="_blank" rel="noreferrer" data-testid={`cr-yt-${r.id}`} className="text-rose-400 hover:underline inline-flex items-center gap-1">
                    <Youtube className="w-3.5 h-3.5" /> Open
                  </a>
                </td>
                <td className="p-4 text-zinc-500 text-sm">{new Date(r.created_at).toLocaleDateString()}</td>
                <td className="p-4"><StatusBadge status={r.status} /></td>
                <td className="p-4 text-right">
                  <Button size="icon" variant="ghost" onClick={() => setEditing({ ...r })} data-testid={`cr-edit-${r.id}`} className="h-8 w-8 text-zinc-400 hover:text-violet-400">
                    <Edit3 className="w-4 h-4" />
                  </Button>
                </td>
              </tr>
            ))}
            {records.length === 0 && <tr><td colSpan="8" className="p-12 text-center text-zinc-500">No copyright requests</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-zinc-950 border-zinc-800 max-w-md" data-testid="cr-edit-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Shield className="w-5 h-5 text-cyan-400" /> Update Request</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div className="p-3 rounded-lg bg-zinc-900 border border-zinc-800 text-sm">
                <div><span className="text-zinc-500">Song: </span>{editing.song_title}</div>
                <div><span className="text-zinc-500">User: </span>{editing.user?.full_name}</div>
                <div className="text-xs text-zinc-500 mt-1">{editing.notes}</div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-zinc-400">Status</Label>
                <Select value={editing.status} onValueChange={(v) => setEditing({ ...editing, status: v })}>
                  <SelectTrigger data-testid="cr-edit-status" className="mt-1.5 bg-zinc-900 border-zinc-800"><SelectValue /></SelectTrigger>
                  <SelectContent>{COPYRIGHT_STATUS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-zinc-400">Admin Notes</Label>
                <Textarea rows={3} data-testid="cr-edit-admin-notes" value={editing.admin_notes || ""} onChange={(e) => setEditing({ ...editing, admin_notes: e.target.value })}
                  className="mt-1.5 bg-zinc-900 border-zinc-800" placeholder="Notes visible to user..." />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} className="border-zinc-700">Cancel</Button>
            <Button onClick={save} data-testid="cr-edit-save" className="bg-gradient-to-r from-cyan-400 to-violet-500 text-zinc-950 font-semibold">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
